// src/pages/Dashboard/Admin/HeuresEnseignement/HeuresEnseignementDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useTeachingHoursDashboard } from "./hooks/useTeachingHoursDashboard";

import DashboardTeachingHoursHeader from "./components/DashboardTeachingHoursHeader";
import TeachingHoursFiltersCard from "./components/TeachingHoursFiltersCard";
import TeachingHoursKpiCards from "./components/TeachingHoursKpiCards";
import TeachingHoursMatrixTable from "./components/TeachingHoursMatrixTable";
import TeachingHoursMobileCards from "./components/TeachingHoursMobileCards";
import TeachingHoursEmptyState from "./components/TeachingHoursEmptyState";
import AddTeachingHoursDrawer from "./components/AddTeachingHoursDrawer";
import EditTeachingHoursDrawer from "./components/EditTeachingHoursDrawer";

import { getTeacherName } from "./utils/teachingHoursUtils";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Heures d’enseignement
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HeuresEnseignementDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    teachers,
    visibleTeachers,
    hours,
    visibleHours,
    monthOptions,
    monthSections,
    hoursIndex,
    monthlyTotalsByTeacher,
    totalsByTeacher,
    stats,
    filters,
    setFilters,
    loading,
    addDrawerOpen,
    editDrawerOpen,
    selectedHour,
    saving,
    deletingId,
    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,
    handleCreateHours,
    handleUpdateHours,
    handleDeleteHour,
    handleDeleteAllHours,
    refreshData,
  } = useTeachingHoursDashboard();

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardTeachingHoursHeader />

        <TeachingHoursFiltersCard
          teachers={teachers}
          monthOptions={monthOptions}
          filters={filters}
          setFilters={setFilters}
          loading={loading}
          onAdd={openAddDrawer}
          onDeleteAll={handleDeleteAllHours}
          onRefresh={refreshData}
        />

        <TeachingHoursKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Tableau de suivi
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Synthèse hebdomadaire par enseignant
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Chaque cellule représente les heures saisies pour une semaine
                  et un enseignant. Le tableau est horizontalement scrollable
                  sur les petits écrans.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Lignes affichées :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {visibleHours.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : visibleHours.length === 0 ? (
              <TeachingHoursEmptyState onAdd={openAddDrawer} />
            ) : (
              <div className="mt-5">
                <TeachingHoursMatrixTable
                  teachers={visibleTeachers}
                  monthSections={monthSections}
                  hoursIndex={hoursIndex}
                  monthlyTotalsByTeacher={monthlyTotalsByTeacher}
                  totalsByTeacher={totalsByTeacher}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteHour}
                  deletingId={deletingId}
                />

                <TeachingHoursMobileCards
                  teachers={visibleTeachers}
                  monthSections={monthSections}
                  hoursIndex={hoursIndex}
                  monthlyTotalsByTeacher={monthlyTotalsByTeacher}
                  totalsByTeacher={totalsByTeacher}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteHour}
                  deletingId={deletingId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <AddTeachingHoursDrawer
        isOpen={addDrawerOpen}
        onClose={closeAddDrawer}
        teachers={teachers}
        existingHours={hours}
        selectedTeacherId={filters.teacherId}
        onSubmit={handleCreateHours}
        saving={saving}
      />

      <EditTeachingHoursDrawer
        isOpen={editDrawerOpen}
        onClose={closeEditDrawer}
        hour={selectedHour}
        teacherName={getTeacherName(teachers, selectedHour?.adherent_id)}
        onSubmit={handleUpdateHours}
        onDelete={handleDeleteHour}
        saving={saving}
        deletingId={deletingId}
      />

      <ToastContainer />
    </section>
  );
}